/**
 * CorrespondenceCard - Sprint Y Block E
 * 
 * Compact representation of a correspondence item.
 * Used in mission detail sidebar, authorization detail, pilot detail.
 * 
 * HITL CRITICAL: Visually distinguishes PROPOSED vs CONFIRMED links.
 * Proposed data is badged as "navrženo — k ověření" / "proposed — pending verification"
 */

import { useLanguage } from "../../contexts/LanguageContext";
import { 
  Send, Inbox, Clock, CheckCircle, XCircle, AlertTriangle,
  Mail, Paperclip, ExternalLink, AlertCircle
} from "lucide-react";
import { Badge } from "../ui/badge";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../ui/tooltip";

// Icon mapping for sources - lucide-react icons only (Rule 19)
const SOURCE_ICONS = {
  regulator: Mail,
  atc: Send,
  operator_authority: Mail,
  police: AlertTriangle,
  fire: AlertCircle,
  energy: AlertCircle,
  insurance: Mail,
  other: Mail,
};

// Color mapping for sources
const SOURCE_COLORS = {
  regulator: "text-blue-500",
  atc: "text-purple-500",
  operator_authority: "text-teal-500",
  police: "text-red-500",
  fire: "text-orange-500",
  energy: "text-yellow-500",
  insurance: "text-green-500",
  other: "text-slate-500",
};

// Direction icons
const DIRECTION_ICONS = {
  inbound: Inbox,
  outbound: Send,
};

// Status icons
const STATUS_ICONS = {
  pending_review: Clock,
  reviewed: CheckCircle,
  linked: CheckCircle,
  archived: CheckCircle,
};

export const CorrespondenceCard = ({ 
  correspondence, 
  onViewDetails,
  compact = false,
  selected = false 
}) => {
  const { translate: t, language } = useLanguage();
  
  if (!correspondence) return null;
  
  const {
    id,
    subject,
    sender_email,
    sender_name,
    received_at,
    direction = "inbound",
    status = "pending_review",
    source_category,
    source_name_cs,
    source_name_en,
    attachments = [],
    extracted = {},
    needs_review,
    proposed_authorization_id,
    proposed_mission_id,
    proposed_pilot_id,
    confirmed_authorization_id,
    confirmed_mission_id,
    confirmed_pilot_id,
  } = correspondence;
  
  // Determine source name based on language
  const sourceName = language === "cs" ? source_name_cs : source_name_en;
  
  // Get appropriate icons
  const SourceIcon = SOURCE_ICONS[source_category] || Mail;
  const sourceColor = SOURCE_COLORS[source_category] || "text-slate-500";
  const DirectionIcon = DIRECTION_ICONS[direction] || Inbox;
  const StatusIcon = STATUS_ICONS[status] || Clock;
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return t("correspondence.today");
    if (diffDays === 1) return t("correspondence.yesterday");
    if (diffDays < 7) return t("correspondence.daysAgo").replace("{days}", diffDays);
    
    return date.toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US", {
      day: "numeric",
      month: "short",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };
  
  // Check for proposed vs confirmed links
  const hasProposedLinks = proposed_authorization_id || proposed_mission_id || proposed_pilot_id;
  const hasConfirmedLinks = confirmed_authorization_id || confirmed_mission_id || confirmed_pilot_id;
  
  // Status badge color
  const statusBadgeVariant = {
    pending_review: "outline",
    reviewed: "secondary",
    linked: "default",
    archived: "secondary",
  }[status] || "outline";
  
  if (compact) {
    // Compact view for list - Language A: thin border, calmer bg
    return (
      <div 
        className={`flex items-start gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
          selected 
            ? "border-slate-400 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/50" 
            : "border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
        }`}
        onClick={() => onViewDetails?.(correspondence)}
        data-testid={`correspondence-card-compact-${id}`}
      >
        <div className={`mt-0.5 ${sourceColor}`}>
          <DirectionIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{subject}</p>
          <p className="text-xs text-muted-foreground truncate">
            {sender_name || sender_email}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">{formatDate(received_at)}</span>
            {attachments.length > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                <Paperclip className="w-3 h-3" />
                {attachments.length}
              </span>
            )}
            {needs_review && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <AlertCircle className="w-3 h-3 text-amber-500" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("correspondence.needsReview")}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Full card view - Language A: thin border, neutral bg
  return (
    <Card 
      className="overflow-hidden border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 transition-colors cursor-pointer"
      onClick={() => onViewDetails?.(correspondence)}
      data-testid={`correspondence-card-${id}`}
    >
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <div className={`mt-1 ${sourceColor}`}>
              <SourceIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="font-medium text-sm leading-tight line-clamp-2">{subject}</h4>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {direction === "inbound" ? t("correspondence.from") : t("correspondence.to")}: {sender_name || sender_email}
              </p>
              {sourceName && (
                <p className="text-xs text-muted-foreground truncate">
                  {sourceName}
                </p>
              )}
            </div>
          </div>
          
          {/* Status & direction */}
          <div className="flex flex-col items-end gap-1 shrink-0">
            <Badge variant={statusBadgeVariant} className="text-xs">
              <StatusIcon className="w-3 h-3 mr-1" />
              {t(`correspondence.status.${status}`)}
            </Badge>
            <span className="text-xs text-muted-foreground">{formatDate(received_at)}</span>
          </div>
        </div>
        
        {/* Extracted summary */}
        {extracted?.summary_cs || extracted?.summary_en ? (
          <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
            {language === "cs" ? extracted.summary_cs : extracted.summary_en}
          </p>
        ) : null}
        
        {/* HITL: Proposed vs Confirmed links - CRITICAL DISTINCTION */}
        {(hasProposedLinks || hasConfirmedLinks) && (
          <div className="mt-3 pt-3 border-t border-border/50">
            {/* Confirmed links */}
            {hasConfirmedLinks && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {confirmed_authorization_id && (
                  <Badge variant="default" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.authorization")} — {t("correspondence.confirmedBadge")}
                  </Badge>
                )}
                {confirmed_mission_id && (
                  <Badge variant="default" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.mission")} — {t("correspondence.confirmedBadge")}
                  </Badge>
                )}
                {confirmed_pilot_id && (
                  <Badge variant="default" className="text-xs bg-green-500/10 text-green-600 border-green-500/30">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.pilot")} — {t("correspondence.confirmedBadge")}
                  </Badge>
                )}
              </div>
            )}
            
            {/* Proposed links - CLEARLY BADGED as "navrženo / proposed" */}
            {hasProposedLinks && (
              <div className="flex flex-wrap gap-1.5">
                {proposed_authorization_id && !confirmed_authorization_id && (
                  <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.authorization")} — {t("correspondence.proposedBadge")}
                  </Badge>
                )}
                {proposed_mission_id && !confirmed_mission_id && (
                  <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.mission")} — {t("correspondence.proposedBadge")}
                  </Badge>
                )}
                {proposed_pilot_id && !confirmed_pilot_id && (
                  <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {t("correspondence.pilot")} — {t("correspondence.proposedBadge")}
                  </Badge>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Footer with attachments and actions */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {attachments.length > 0 && (
              <span className="flex items-center gap-1">
                <Paperclip className="w-3 h-3" />
                {attachments.length} {t("correspondence.attachment")}{attachments.length > 1 ? "s" : ""}
              </span>
            )}
            {needs_review && (
              <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                <Clock className="w-3 h-3 mr-1" />
                {t("correspondence.needsReview")}
              </Badge>
            )}
          </div>
          
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs h-7"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails?.(correspondence);
            }}
            data-testid={`correspondence-view-${id}`}
          >
            {t("correspondence.viewDetails")}
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CorrespondenceCard;
